
import { Button } from '@atd/rui-base.mui.button'
import { Fab } from '@atd/rui-base.mui.fab'
import { IconButton } from '@atd/rui-base.mui.icon-button'
import React, { useEffect, useState } from 'react'
import {
    buildDatePickerDates,
    buildQueryString,
    dateConstantValues,
    getDefaultFilterState,
    participantActivityValues,
    setFilterFromQueryString,
} from './queryString'
import {setFilteredJobs, setJobListFilter} from './workorderActions'
import { useDispatch, useSelector } from 'react-redux'

import { MenuItem } from '@atd/rui-base.mui.menu-item'
import { Tab } from '@atd/rui-base.mui.tab'
import { Tabs } from '@atd/rui-base.mui.tabs'
import { TextField } from '@atd/rui-base.mui.text-field'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Cancel'
import DatePicker from '../commonComponents/lazyDatePicker'
import FormControl from '@mui/material/FormControl'
import GffFilter from '../gffManager/gffFilter'
import InputLabel from '@mui/material/InputLabel'
import JobListSort from './jobListSort'
import MinimizeIcon from '@mui/icons-material/Remove'
import ResetFilters from './resetFilters'
import Select from '@mui/material/Select'
import SortIcon from '@mui/icons-material/Sort'
import {fetchAllSystems} from '../systemManager/systemActions'
import {fetchMixes} from '../productManager/mixActions'
import {selectMixes} from '../productManager/productReducer'

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth <= 610

const JobListFilter = () => {
    const dispatch = useDispatch()

    const [filterCollapsed, setFilterCollapsed] = useState(isMobileViewport)
    const [sortCollapsed, setSortCollapsed] = useState(isMobileViewport)
    const [fullCollapsed, setFullCollapsed] = useState(isMobileViewport)
    const [filteredJobs, setFilteredJobsState] = useState([])

    const workorderStore = useSelector(store => store.workorder)
    const systemStore = useSelector(store => store.system)
    const adminStore = useSelector(store => store.admin)

    const {allSystems} = systemStore
    const mixes = useSelector(selectMixes)
    const {workorders: jobs, isFetching, jobListFilter} = workorderStore
    const {selectedCompanyId, isJobFilterFromURL} = adminStore

    let systems = []
    if (Array.isArray(allSystems)) {
        systems = [...allSystems]
    }

    const onJobsSorted = (sortedJobs, sortOrder, sortSelection, isSortFilterChanged) => {
        dispatch(setFilteredJobs(sortedJobs))
        if (jobListFilter.sortOrder.toString() !== sortOrder.toString()) {
            jobListFilter.sortOrder = sortOrder
            if (isSortFilterChanged) {
                buildQueryString(jobListFilter)
            }
        }
    }

    const onFilterChanged = (filter) => {
        const oldFilter = jobListFilter
        dispatch(setJobListFilter(Object.assign({}, oldFilter, filter), oldFilter))
    }

    const onExpandFilter = (event) => {
        event.preventDefault()
        setFullCollapsed(!filterCollapsed && sortCollapsed)
        setFilterCollapsed(!filterCollapsed)
    }

    const onExpandSort = (event) => {
        event.preventDefault()
        setFullCollapsed(filterCollapsed && !sortCollapsed)
        setSortCollapsed(!sortCollapsed)
    }
    // In each date change handler, make sure the dates end up with the
    // start date occurring before the end date. Otherwise all jobs will be filtered out.
    const handleChangeStart = (date) => {
        jobListFilter.selectedDateConstant = 0
        if (jobListFilter.endDate && date > jobListFilter.endDate) {
            onFilterChanged({startDate: jobListFilter.endDate, endDate: date})
        }
        else {
            onFilterChanged({startDate: date})
        }
    }

    const handleChangeEnd = (date) => {
        jobListFilter.selectedDateConstant = 0
        if (jobListFilter.startDate && date < jobListFilter.startDate) {
            onFilterChanged({startDate: date, endDate: jobListFilter.startDate})
        }
        else {
            onFilterChanged({endDate: date})
        }
    }
    const handleCalenderClick = (date) => {
        jobListFilter.selectedDateConstant = 0
        if ((jobListFilter.startDate && jobListFilter.endDate) ||
            (jobListFilter.startDate === null && jobListFilter.endDate === null)
        ) {
            onFilterChanged({startDate: date, endDate: null})
        }
        else if (jobListFilter.startDate && date > jobListFilter.startDate) {
            onFilterChanged({endDate: date})
        }
        else if (jobListFilter.startDate && date < jobListFilter.startDate) {
            onFilterChanged({startDate: date, endDate: jobListFilter.startDate})
        }
        else if (jobListFilter.endDate && date < jobListFilter.endDate) {
            onFilterChanged({startDate: date})
        }
        else if (jobListFilter.endDate && date > jobListFilter.endDate) {
            onFilterChanged({startDate: jobListFilter.endDate, endDate: date})
        }
        else {
            onFilterChanged({startDate: date, endDate: date})
        }
    }

    const getSelectedAvailability = () => {
        switch (Number(jobListFilter.selectedAvailability)) {
            case 0:
                return 'Unarchived'
            case 1:
                return 'Archived'
            case 2:
                return 'All'
            default:
                return 'All'
        }
    }

    const getSelectedDateConstant = () => {
        let val
        switch (jobListFilter.selectedDateConstant) {
            case dateConstantValues[0]:
                val = 'None Selected'
                break
            case dateConstantValues[1]:
                val = 'Day'
                break
            case dateConstantValues[2]:
                val = 'Week'
                break
            case dateConstantValues[3]:
                val = 'Month'
                break
            case dateConstantValues[4]:
                val = 'Year'
                break
            default:
                val = 'None Selected'
        }
        if (jobListFilter.selectedDateConstant != 0) {
            buildDatePickerDates(jobListFilter)
        }

        return val
    }

    const toMenuItem = (list, selectedItems, labelKey = 'name', altKey = 'externalDeviceId') => {
        return list.map((data, i) => {
            return (
                <MenuItem
                    key={data.id}
                    insetChildren={true}
                    value={data.id}
                    name={data.id}
                    checked={selectedItems.includes(data.id)}
                >
                    {data[labelKey] || data[altKey]}
                </MenuItem>
            )
        })
    }

    const buildURL = (filter) => {
        if (filter.selectedCompanyId !== selectedCompanyId) {
            filter.selectedCompanyId = selectedCompanyId
            buildQueryString(filter)
            onFilterChanged(filter)
        }
    }

    const resetDateParams = () => {
        jobListFilter.startDate = null
        jobListFilter.endDate = null
        jobListFilter.selectedDateConstant = 0
        jobListFilter.jobName = ''
        onFilterChanged({selectedDateConstant: dateConstantValues[0]})
    }

    const renderSortBody = () => {
        return (
            <div className="jobFilter" style={sortCollapsed ? {display: 'none'} : undefined}>
                <div className="filterGroup">
                    <JobListSort
                        jobs={filteredJobs}
                        onJobsSorted={onJobsSorted}
                    />
                </div>
            </div>
        )
    }

    const renderFilterBody = () => {
        if (filterCollapsed) return
        const systemMenuItems = toMenuItem(systems, jobListFilter.selectedSystems)

        const InvisibleContainer = () => {
            return (
                <></>
            )
        }

        return (
            <div className="jobFilter" >
                <div className="filterGroup">
                    <div style={{ alignSelf: 'flex-start' }}>Search:</div>
                    <div style={{ display: 'flex', width: '95%' }}>
                        <TextField
                            value={jobListFilter.jobName || ''}
                            name="jobName"
                            label="Job Name"
                            variant="standard"
                            fullWidth
                            onChange={(e) => { onFilterChanged({jobName: e.target.value}) }}
                        />
                        <IconButton disabled={!jobListFilter.jobName}
                            className="clearSelection"
                            name="clearJobName"
                            onClick={() => { onFilterChanged({jobName: ''}) }}>
                            <ClearIcon/>
                        </IconButton>
                    </div>
                </div>
                <div className="groupDivider" />
                <div className="filterGroup">
                    <GffFilter
                        noGFFonly={jobListFilter.noGFFonly}
                        selectedGrowers={jobListFilter.selectedGrowers}
                        selectedFarms={jobListFilter.selectedFarms}
                        selectedFields={jobListFilter.selectedFields}
                        onGffFilterChanged={onFilterChanged}
                    />
                </div>
                <div className="groupDivider"/>
                <div className="filterGroup">
                    <div style={{display: 'flex', margin: '15px 0 5px 0', width: '95%'}}>
                        <FormControl variant="standard" fullWidth>
                            <InputLabel id="system-label">System</InputLabel>
                            <Select
                                value={jobListFilter.selectedSystems}
                                onChange={(event) => {
                                    onFilterChanged({selectedSystems: event.target.value})
                                }}
                                name="systemDropdown"
                                multiple
                                className="autoCompleteFilter"
                            >
                                {systemMenuItems}
                            </Select>
                        </FormControl>

                        <IconButton
                            disabled={!jobListFilter.selectedSystems.length}
                            className="clearSelection"
                            name="clearSystems"
                            onClick={() => { onFilterChanged({selectedSystems: []}) }}
                            style={{marginTop: '10px'}}>
                            <ClearIcon/>
                        </IconButton>
                    </div>
                </div>
                <div className="groupDivider"/>
                <div className="filterGroup">
                    <div style={{display: 'flex', margin: '15px 0 5px 0', width: '95%'}}>
                        <FormControl variant="standard" fullWidth>
                            <InputLabel id="product-label">Product</InputLabel>
                            <Select
                                className="autoCompleteFilter"
                                value={jobListFilter.selectedMixes}
                                onChange={(event) => {
                                    onFilterChanged({selectedMixes: event.target.value})
                                }}
                                name="productDropdown"
                                multiple
                            >
                                {toMenuItem(mixes, jobListFilter.selectedMixes)}
                            </Select>
                        </FormControl>
                        <IconButton
                            disabled={!jobListFilter.selectedMixes.length}
                            className="clearSelection"
                            name="clearProducts"
                            onClick={() => { onFilterChanged({selectedMixes: []}) }}
                            style={{marginTop: '10px'}}>
                            <ClearIcon/>
                        </IconButton>
                    </div>
                </div>
                <div className="groupDivider"/>
                <div className="filterGroup">
                    <div style={{alignSelf: 'flex-start'}}>Show Last: <span style={{color: 'var(--primary)'}}>{getSelectedDateConstant()}</span></div>
                    <div style={{display: 'flex', width: '100%'}}>
                        <Tabs
                            variant="fullWidth"
                            name="dateParameterConstantFilter"
                            className="radioFilterRow"
                            TabIndicatorProps={{style: {display: 'none'}}}
                            value={jobListFilter.selectedDateConstant === dateConstantValues[0] ? false : jobListFilter.selectedDateConstant}
                            onChange={(_, selectedDateConstant) => onFilterChanged({selectedDateConstant})}
                        >
                            <Tab
                                className={jobListFilter.selectedDateConstant === dateConstantValues[1] ? 'clickedOption' : 'clickableOption'}
                                value={dateConstantValues[1]}
                                label="Day"
                                name="lastDayButton"
                            />
                            <Tab
                                className={jobListFilter.selectedDateConstant === dateConstantValues[2] ? 'clickedOption' : 'clickableOption'}
                                value={dateConstantValues[2]}
                                label="Week"
                                name="lastWeekButton"
                            />
                            <Tab
                                className={jobListFilter.selectedDateConstant === dateConstantValues[3] ? 'clickedOption' : 'clickableOption'}
                                value={dateConstantValues[3]}
                                label="Month"
                                name="lastMonthButton"
                            />
                            <Tab
                                className={jobListFilter.selectedDateConstant === dateConstantValues[4] ? 'clickedOption' : 'clickableOption'}
                                value={dateConstantValues[4]}
                                label="Year"
                                name="lastYearButton"
                            />
                        </Tabs>
                        <IconButton disabled={jobListFilter.selectedDateConstant === dateConstantValues[0]}
                            className="clearSelection"
                            name="clearDateParams"
                            onClick={() => { resetDateParams() }}
                            style={{ bottom: '6px' }}>
                            <ClearIcon />
                        </IconButton>
                    </div>
                    <div className="flexArea">
                        <DatePicker
                            selected={jobListFilter.startDate}
                            selectsStart
                            startDate={jobListFilter.startDate}
                            endDate={jobListFilter.endDate}
                            isClearable={true}
                            maxDate={new Date()}
                            onChange={handleChangeStart}
                            className="flexItem"
                            calendarContainer={InvisibleContainer}
                        />
                        <DatePicker
                            selected={jobListFilter.endDate}
                            selectsEnd
                            startDate={jobListFilter.startDate}
                            endDate={jobListFilter.endDate}
                            isClearable={true}
                            maxDate={new Date()}
                            onChange={handleChangeEnd}
                            className="flexItem"
                            calendarContainer={InvisibleContainer}
                        />
                    </div>
                    <DatePicker
                        inline
                        startDate={jobListFilter.startDate}
                        endDate={jobListFilter.endDate}
                        maxDate={new Date()}
                        disabled={true}
                        onChange={handleCalenderClick}
                    />
                </div>
                <div className="groupDivider"/>
                <div className="filterGroup">
                    <div style={{alignSelf: 'flex-start'}}>Availability: <span style={{color: 'var(--primary)'}}>{getSelectedAvailability()}</span></div>
                    <Tabs
                        variant="fullWidth"
                        name="jobAvailabilityFilter"
                        className="radioFilterRow"
                        TabIndicatorProps={{style: {display: 'none'}}}
                        value={Number(jobListFilter.selectedAvailability)}
                        onChange={(_, selectedAvailability) => onFilterChanged({selectedAvailability})}
                    >
                        <Tab
                            className={jobListFilter.selectedAvailability == 2 ? 'clickedOption' : 'clickableOption'}
                            value={2}
                            label="All"
                            name="allAvailabilityButton"
                        />
                        <Tab
                            className={jobListFilter.selectedAvailability == 0 ? 'clickedOption' : 'clickableOption'}
                            value={0}
                            name="summaryViewButton"
                            icon={<img className="filterOption" src={`${process.env.PUBLIC_URL}/images/jobCard/JobCard_BtnUnarchiveJobWhite.svg`}/>}
                        />
                        <Tab
                            className={jobListFilter.selectedAvailability == 1 ? 'clickedOption' : 'clickableOption'}
                            value={1}
                            name="summaryViewButton"
                            icon={<img className="filterOption" src={`${process.env.PUBLIC_URL}/images/jobCard/JobCard_BtnArchiveJobWhite.svg`}/>}
                        />
                    </Tabs>
                    <div style={{ alignSelf: 'flex-start' }}>Participant Activity: <span style={{ color: 'var(--primary)' }}>{jobListFilter.selectedParticipantActivity}</span></div>
                    <Tabs
                        variant="fullWidth"
                        name="jobActivityFilter"
                        className="radioFilterRow"
                        TabIndicatorProps={{style: {display: 'none'}}}
                        value={jobListFilter.selectedParticipantActivity}
                        onChange={(_, selectedParticipantActivity) => onFilterChanged({selectedParticipantActivity})}
                    >
                        <Tab
                            className={jobListFilter.selectedParticipantActivity === participantActivityValues[0] ? 'clickedOption' : 'clickableOption'} // all
                            value="all"
                            label="All"
                            name="allActivityFilterButton"
                        />
                        <Tab
                            className={jobListFilter.selectedParticipantActivity === participantActivityValues[1] ? 'clickedOption' : 'clickableOption'} // none
                            value="none"
                            name="newFilterButton"
                            icon={<img className="filterOption" src={`${process.env.PUBLIC_URL}/images/jobCard/JobCard_StatusNewWhite.svg`}/>}
                        />
                        <Tab
                            className={jobListFilter.selectedParticipantActivity === participantActivityValues[2] ? 'clickedOption' : 'clickableOption'} // in progress
                            value="in progress"
                            name="inProgressFilterButton"
                            icon={<img className="filterOption" src={`${process.env.PUBLIC_URL}/images/jobCard/JobCard_StatusActiveWhite.svg`}/>}
                        />
                        <Tab
                            className={jobListFilter.selectedParticipantActivity === participantActivityValues[3] ? 'clickedOption' : 'clickableOption'} // inactive
                            value="inactive"
                            name="inactiveFilterButton"
                            label="Inactive"
                        />
                    </Tabs>
                </div>
                <div className="groupDivider"/>
                <ResetFilters />
            </div>
        )
    }

    useEffect(() => {
        dispatch(fetchMixes())
    }, [])

    useEffect(() => {
        if (!isFetching) {
            setFilteredJobsState([...jobs])
        }
    }, [jobs, isFetching])

    // To apply the current filters when a company is changed
    useEffect(() => {
        dispatch(fetchAllSystems())
        if (selectedCompanyId) {
            if (isJobFilterFromURL) {
                jobListFilter.selectedDateConstant = 0
                const filter = setFilterFromQueryString(getDefaultFilterState())
                buildURL(filter)
            }
            else {
                jobListFilter.selectedGrowers = []
                jobListFilter.selectedFarms = []
                jobListFilter.selectedFields = []
                jobListFilter.selectedSystems = []
                jobListFilter.selectedMixes = []
                buildURL(jobListFilter)
            }
        }
    }, [selectedCompanyId, isJobFilterFromURL])


    return (
        <div className="filterArea">
            <div className="floatingAdd">
                <Button
                    variant="contained"
                    href={'/jobs/create'}
                    startIcon={<AddIcon htmlColor="white"/>}
                    color="primary"
                    className={fullCollapsed ? 'hidden' : ''}
                    fullWidth>
                    Create
                </Button>
                <Fab
                    href={'/jobs/create'}
                    sx={{backgroundColor: 'var(--primary)', '&:hover': {backgroundColor: 'var(--primary)'}}}
                    className={fullCollapsed ? 'floatingAdd' : 'hidden'}>
                    <AddIcon htmlColor="white"/>
                </Fab>
            </div>
            <div
                style={{backgroundColor: 'var(--primary)', display: 'flex', justifyContent: 'space-between'}}
                className={fullCollapsed ? 'filterCollapsed' : ''}
                onClick={onExpandSort}
            >
                <Button
                    variant="text"
                    onClick={onExpandSort}
                    style={{color: 'white'}}
                    startIcon={<SortIcon htmlColor="white"/>}
                    className={fullCollapsed ? 'filterCollapsed' : ''}>
                    Sort
                </Button>
                <Button
                    variant="text"
                    onClick={onExpandSort}
                    style={{color: 'white', minWidth: '48px'}}
                    className={fullCollapsed ? 'filterCollapsed' : ''}>
                    {sortCollapsed ? <AddIcon htmlColor="white"/> : <MinimizeIcon htmlColor="white"/>}
                </Button>
            </div>
            {renderSortBody()}
            <div className="floatingAdd">
                <Fab
                    onClick={onExpandSort}
                    sx={{backgroundColor: 'var(--primary)', '&:hover': {backgroundColor: 'var(--primary)'}}}
                    className={fullCollapsed ? 'filterIconButton' : 'hidden'}>
                    <SortIcon htmlColor="white"/>
                </Fab>
            </div>
            <div
                style={{backgroundColor: 'var(--primary)', display: 'flex', justifyContent: 'space-between'}}
                className={fullCollapsed ? 'filterCollapsed' : ''}
                onClick={onExpandFilter}
            >
                <Button
                    variant="text"
                    onClick={onExpandFilter}
                    style={{color: 'white'}}
                    startIcon={<img className="standardIcon" src={`${process.env.PUBLIC_URL}/images/jobIcons/Job_Filter.svg`}/>}
                    className={fullCollapsed ? 'filterCollapsed' : ''}>
                    Filter
                </Button>
                <Button
                    variant="text"
                    onClick={onExpandFilter}
                    style={{color: 'white', minWidth: '48px'}}
                    className={fullCollapsed ? 'filterCollapsed' : ''}>
                    {filterCollapsed ? <AddIcon htmlColor="white"/> : <MinimizeIcon htmlColor="white"/>}
                </Button>
            </div>
            <Fab
                onClick={onExpandFilter}
                sx={{backgroundColor: 'var(--primary)', '&:hover': {backgroundColor: 'var(--primary)'}}}
                className={fullCollapsed ? 'filterIconButton' : 'hidden'}>
                <img style={{height: '24px', padding: '15px'}} src={`${process.env.PUBLIC_URL}/images/jobIcons/Job_Filter.svg`}/>
            </Fab>
            {renderFilterBody()}
        </div>
    )
}

export default JobListFilter
